import { useEffect, useRef } from "react";
import { Flex, Form, Rate } from "antd";
import { QuestionCircleOutlined, StarFilled, HeartFilled, LikeFilled, SmileFilled } from "@ant-design/icons";
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";

const RATING_CHARACTERS = {
    star:  <StarFilled />,
    heart: <HeartFilled />,
    thumb: <LikeFilled />,
    emoji: <SmileFilled />,
};

const RATING_COLORS = {
    star:  "#FAAD14",
    heart: "#EB2F96",
    thumb: "#1890ff",
    emoji: "#FAAD14",
};

const RATING_TOOLTIPS = {
    star:  ["Terrible", "Bad", "Normal", "Good", "Wonderful"],
    heart: ["Dislike", "Okay", "Like", "Love", "Adore"],
    thumb: ["Very Bad", "Bad", "Average", "Good", "Excellent"],
    emoji: ["Awful", "Bad", "Okay", "Good", "Amazing"],
};

const RatingInput = (props) => {
    const {
        label,
        tooltip,
        helpText,
        mandatory,
        readOnly,
        previewMode = false,
        id,
        form,
        setValueData,
        count = 5,
        allowHalf = false,
        ratingType = "star",
    } = props;

    const rules = mandatory
        ? [{ required: true, message: `${label || "Rating"} is required` }]
        : undefined;

    const help = tooltip
        ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;

    const disabled = previewMode && readOnly;

    let activeSetValue = getActiveSetValue(setValueData);

    // The rating the condition last wrote, so it can be taken back once the
    // condition stops passing without discarding a rating the user gave since.
    const appliedFormValueRef = useRef(undefined);

    useEffect(() => {
        if (activeSetValue?.value !== undefined && activeSetValue?.value !== null) {
            form?.setFieldValue(id, activeSetValue.value);
            appliedFormValueRef.current = activeSetValue.value;
        } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: form?.getFieldValue(id), appliedValue: appliedFormValueRef.current })) {
            form?.setFieldValue(id, getConditionalRevertValue(id, props.defaultFormValues));
            appliedFormValueRef.current = undefined;
        } else {
            appliedFormValueRef.current = undefined;
        }
    }, [activeSetValue, disabled, form, id]);

    const safeCount = Math.min(Math.max(Number(count) || 5, 1), 10);
    const color = RATING_COLORS[ratingType] || RATING_COLORS.star;
    const character = RATING_CHARACTERS[ratingType] || RATING_CHARACTERS.star;

    // Build tooltips array sized to count
    const baseTooltips = RATING_TOOLTIPS[ratingType] || RATING_TOOLTIPS.star;
    const tooltips = Array.from({ length: safeCount }, (_, i) =>
        baseTooltips[i] ?? `${i + 1}`
    );

    return (
        <div className="form-element w-full">
            <Flex vertical className="w-full">
                <Form.Item
                    name={id}
                    label={label}
                    tooltip={help}
                    rules={rules}
                    className="w-full mb-0"
                >
                    <Rate
                        count={safeCount}
                        allowHalf={allowHalf}
                        tooltips={tooltips}
                        disabled={disabled}
                        character={character}
                        className={`text-2xl leading-none ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                        style={{ color }}
                    />
                </Form.Item>
            </Flex>
        </div>
    );
};

export default RatingInput;
